import { BarChart3, TrendingUp, DollarSign, Calendar, Download, Loader2, Users, Target, ArrowUpRight, ArrowDownRight, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  LineChart,
  Line,
  Legend,
  Funnel,
  FunnelChart,
  LabelList,
} from "recharts";
import {
  usePipelineMetrics,
  useMonthlyPipelineData,
  useStageDistribution,
  useLeadConversionData,
  useForecastData,
  useLeadStats,
} from "@/hooks/useAnalytics";
import { AICompetitorAnalysis } from "@/components/ai/AICompetitorAnalysis";
import { AIDealForecast } from "@/components/ai/AIDealForecast";
import { AILearningDashboard } from "@/components/ai/AILearningDashboard";

const COLORS = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function formatCurrency(value: number) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function MetricCard({
  icon: Icon,
  label,
  value,
  change,
  changeLabel,
  isPositive,
}: {
  icon: typeof DollarSign;
  label: string;
  value: string;
  change?: string;
  changeLabel?: string;
  isPositive?: boolean;
}) {
  return (
    <div className="glass rounded-xl p-5">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="h-4 w-4" />
        <span className="text-sm">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {change && (
        <p className={`text-xs mt-1 flex items-center gap-1 ${isPositive ? "text-success" : "text-destructive"}`}>
          {isPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {change} {changeLabel}
        </p>
      )}
    </div>
  );
}

export default function Analytics() {
  const { data: metrics, isLoading: metricsLoading } = usePipelineMetrics();
  const { data: monthlyData, isLoading: monthlyLoading } = useMonthlyPipelineData();
  const { data: stageData, isLoading: stageLoading } = useStageDistribution();
  const { data: conversionData, isLoading: conversionLoading } = useLeadConversionData();
  const { data: forecastData, isLoading: forecastLoading } = useForecastData();
  const { data: leadStats, isLoading: leadStatsLoading } = useLeadStats();

  const isLoading = metricsLoading || monthlyLoading || stageLoading || conversionLoading || forecastLoading || leadStatsLoading;

  // Prepare funnel data for lead stages
  const funnelData = leadStats ? [
    { name: "Total Leads", value: leadStats.total, fill: "hsl(var(--chart-1))" },
    { name: "Qualified", value: leadStats.qualified, fill: "hsl(var(--chart-2))" },
    { name: "Converted", value: leadStats.converted, fill: "hsl(var(--primary))" },
  ] : [];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Analytics</h1>
          <p className="text-muted-foreground">
            Track performance and forecast revenue
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2">
            <Calendar className="h-4 w-4" />
            Last 6 months
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Tabs defaultValue="pipeline" className="space-y-6">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="conversion">Conversion</TabsTrigger>
            <TabsTrigger value="forecast">Forecast</TabsTrigger>
            <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
            <TabsTrigger value="ai-learning" className="gap-2">
              <Brain className="h-3 w-3" />
              AI Learning
            </TabsTrigger>
          </TabsList>

          {/* Pipeline Tab */}
          <TabsContent value="pipeline" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <MetricCard
                icon={DollarSign}
                label="Total Pipeline"
                value={formatCurrency(metrics?.totalPipeline || 0)}
                change="+12%"
                changeLabel="vs last month"
                isPositive
              />
              <MetricCard
                icon={BarChart3}
                label="Avg Deal Size"
                value={formatCurrency(metrics?.avgDealSize || 0)}
                change="+8%"
                changeLabel="vs last month"
                isPositive
              />
              <MetricCard
                icon={TrendingUp}
                label="Win Rate"
                value={`${metrics?.winRate || 0}%`}
                change="+4%"
                changeLabel="vs last quarter"
                isPositive
              />
              <MetricCard
                icon={Calendar}
                label="Avg Cycle"
                value={`${metrics?.avgCycleDays || 0} days`}
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pipeline Over Time */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-semibold mb-6">Pipeline Value Over Time</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={monthlyData || []}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))" 
                      fontSize={12} 
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "hsl(var(--card))", 
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                      formatter={(value: number) => [formatCurrency(value), "Pipeline Value"]}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      fill="url(#colorValue)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Stage Distribution */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-semibold mb-6">Deals by Stage</h3>
                {stageData && stageData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={stageData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="stage"
                      >
                        {stageData.map((entry, index) => (
                          <Cell key={entry.stage} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: "hsl(var(--card))", 
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                        formatter={(value: number) => formatCurrency(value)}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No deals data available
                  </div>
                )}
              </div>
            </div>

            {/* Deal Count Chart */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-semibold mb-6">Deals Created per Month</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlyData || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--card))", 
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar dataKey="deals" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          {/* Conversion Tab */}
          <TabsContent value="conversion" className="space-y-6">
            {/* Lead Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <MetricCard
                icon={Users}
                label="Total Leads"
                value={String(leadStats?.total || 0)}
              />
              <MetricCard
                icon={Target}
                label="New Leads"
                value={String(leadStats?.newLeads || 0)}
              />
              <MetricCard
                icon={TrendingUp}
                label="Qualified"
                value={String(leadStats?.qualified || 0)}
              />
              <MetricCard
                icon={BarChart3}
                label="Conversion Rate"
                value={`${leadStats?.conversionRate || 0}%`}
                isPositive={(leadStats?.conversionRate || 0) > 10}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Lead Funnel */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-semibold mb-6">Lead Funnel</h3>
                {funnelData.length > 0 && funnelData[0].value > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <FunnelChart>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: "hsl(var(--card))", 
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                      />
                      <Funnel
                        dataKey="value"
                        data={funnelData}
                        isAnimationActive
                      >
                        <LabelList position="center" fill="#fff" stroke="none" dataKey="name" />
                      </Funnel>
                    </FunnelChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No lead data available
                  </div>
                )}
              </div>

              {/* Conversion by Source */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-semibold mb-6">Conversion by Source</h3>
                {conversionData && conversionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={conversionData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis 
                        type="category" 
                        dataKey="source" 
                        stroke="hsl(var(--muted-foreground))" 
                        fontSize={12}
                        width={100}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: "hsl(var(--card))", 
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                      />
                      <Legend />
                      <Bar dataKey="leads" fill="hsl(var(--muted-foreground))" name="Total Leads" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="converted" fill="hsl(var(--primary))" name="Converted" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No conversion data available
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Forecast Tab */}
          <TabsContent value="forecast" className="space-y-6">
            <div className="glass rounded-xl p-6">
              <h3 className="font-semibold mb-6">Revenue Forecast</h3>
              <div className="space-y-6">
                {forecastData?.map((item) => {
                  const total = item.committed + item.upside + item.pipeline;
                  return (
                    <div key={item.period} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{item.period}</span>
                        <span className="text-lg font-bold">
                          {formatCurrency(item.committed + item.upside)}
                        </span>
                      </div>
                      <div className="flex h-4 rounded-full overflow-hidden bg-muted">
                        {total > 0 && (
                          <>
                            <div
                              className="bg-success transition-all"
                              style={{ width: `${(item.committed / total) * 100}%` }}
                            />
                            <div
                              className="bg-primary transition-all"
                              style={{ width: `${(item.upside / total) * 100}%` }}
                            />
                            <div
                              className="bg-muted-foreground/30 transition-all"
                              style={{ width: `${(item.pipeline / total) * 100}%` }}
                            />
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <div className="h-2 w-2 rounded-full bg-success" />
                          Committed: {formatCurrency(item.committed)}
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="h-2 w-2 rounded-full bg-primary" />
                          Upside: {formatCurrency(item.upside)}
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="h-2 w-2 rounded-full bg-muted-foreground/30" />
                          Pipeline: {formatCurrency(item.pipeline)}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {(!forecastData || forecastData.every(f => f.committed + f.upside + f.pipeline === 0)) && (
                  <div className="text-center py-8 text-muted-foreground">
                    No forecast data available. Add deals with expected close dates to see forecasts.
                  </div>
                )}
              </div>
            </div>

            {/* Forecast Chart */}
            {forecastData && forecastData.some(f => f.committed + f.upside + f.pipeline > 0) && (
              <div className="glass rounded-xl p-6">
                <h3 className="font-semibold mb-6">Forecast Breakdown</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={forecastData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="period" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))" 
                      fontSize={12}
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "hsl(var(--card))", 
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                      formatter={(value: number) => formatCurrency(value)}
                    />
                    <Legend />
                    <Bar dataKey="committed" stackId="a" fill="hsl(142, 76%, 36%)" name="Committed" />
                    <Bar dataKey="upside" stackId="a" fill="hsl(var(--primary))" name="Upside" />
                    <Bar dataKey="pipeline" stackId="a" fill="hsl(var(--muted-foreground))" name="Pipeline" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </TabsContent>

          {/* AI Insights Tab */}
          <TabsContent value="ai-insights" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AICompetitorAnalysis />
              <AIDealForecast />
            </div>
          </TabsContent>

          {/* AI Learning Tab */}
          <TabsContent value="ai-learning" className="space-y-6">
            <AILearningDashboard />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
